/* eslint-disable max-lines */
/**
 * Utilities to compute analytics for MediExamPro simulations.
 * The functions here produce serialisable, hierarchy-rich summaries
 * that power the in-app review experience and the exam history view.
 */

const DEFAULT_AREA = 'Área sin clasificar';
const DEFAULT_TOPIC = 'Tema sin clasificar';
const DEFAULT_SUBTOPIC = 'Subtema sin clasificar';
const DEFAULT_DETAIL = 'Detalle sin clasificar';
const DEFAULT_ORGAN = 'Sistema sin clasificar';

const STATUS_CORRECT = 'correct';
const STATUS_INCORRECT = 'incorrect';
const STATUS_SKIPPED = 'skipped';

const STATUS_KEYS = [STATUS_CORRECT, STATUS_INCORRECT, STATUS_SKIPPED];

const roundAccuracy = (numerator, denominator) => {
  if (!denominator) return 0;
  const value = (numerator / denominator) * 100;
  return Math.round(value * 10) / 10;
};

const createCounterBase = (key, label) => ({
  key,
  label,
  total: 0,
  correct: 0,
  incorrect: 0,
  skipped: 0,
  questionIndexes: [],
});

const incrementCounter = (container, status) => {
  container.total += 1;
  if (status === STATUS_CORRECT) {
    container.correct += 1;
  } else if (status === STATUS_INCORRECT) {
    container.incorrect += 1;
  } else {
    container.skipped += 1;
  }
};

const ensureMapEntry = (map, key, label, factory) => {
  if (!map.has(key)) {
    map.set(key, factory(key, label));
  }
  return map.get(key);
};

const cloneQuestion = (question) => {
  if (!question || typeof question !== 'object') return {};
  const cloned = { ...question };
  if (Array.isArray(question.options)) {
    cloned.options = question.options.map((opt) => ({ ...opt }));
  } else {
    cloned.options = [];
  }
  return cloned;
};

const finaliseSimpleCollection = (map) =>
  Array.from(map.values())
    .map((entry) => ({
      key: entry.key,
      label: entry.label,
      total: entry.total,
      correct: entry.correct,
      incorrect: entry.incorrect,
      skipped: entry.skipped,
      accuracy: roundAccuracy(entry.correct, entry.total),
    }))
    .sort((a, b) => {
      if (b.total !== a.total) return b.total - a.total;
      return a.label.localeCompare(b.label, 'es');
    });

const finaliseDetailCollection = (map) => {
  const safeMap = map instanceof Map ? map : new Map();
  return Array.from(safeMap.values())
    .map((detail) => ({
      key: detail.key,
      label: detail.label,
      total: detail.total,
      correct: detail.correct,
      incorrect: detail.incorrect,
      skipped: detail.skipped,
      accuracy: roundAccuracy(detail.correct, detail.total),
      questionIndexes: [...detail.questionIndexes],
    }))
    .sort((a, b) => {
      if (b.total !== a.total) return b.total - a.total;
      return a.label.localeCompare(b.label, 'es');
    });
};

const finaliseSubtopicCollection = (map) => {
  const safeMap = map instanceof Map ? map : new Map();
  return Array.from(safeMap.values())
    .map((subtopic) => ({
      key: subtopic.key,
      label: subtopic.label,
      total: subtopic.total,
      correct: subtopic.correct,
      incorrect: subtopic.incorrect,
      skipped: subtopic.skipped,
      accuracy: roundAccuracy(subtopic.correct, subtopic.total),
      questionIndexes: [...subtopic.questionIndexes],
      details: finaliseDetailCollection(subtopic.details),
    }))
    .sort((a, b) => {
      if (b.total !== a.total) return b.total - a.total;
      return a.label.localeCompare(b.label, 'es');
    });
};

const finaliseTopicCollection = (map) => {
  const safeMap = map instanceof Map ? map : new Map();
  return Array.from(safeMap.values())
    .map((topic) => ({
      key: topic.key,
      label: topic.label,
      total: topic.total,
      correct: topic.correct,
      incorrect: topic.incorrect,
      skipped: topic.skipped,
      accuracy: roundAccuracy(topic.correct, topic.total),
      questionIndexes: [...topic.questionIndexes],
      subtopics: finaliseSubtopicCollection(topic.subtopics),
    }))
    .sort((a, b) => {
      if (b.total !== a.total) return b.total - a.total;
      return a.label.localeCompare(b.label, 'es');
    });
};

const finaliseAreaCollection = (map) => {
  const safeMap = map instanceof Map ? map : new Map();
  return Array.from(safeMap.values())
    .map((area) => ({
      key: area.key,
      label: area.label,
      total: area.total,
      correct: area.correct,
      incorrect: area.incorrect,
      skipped: area.skipped,
      accuracy: roundAccuracy(area.correct, area.total),
      questionIndexes: [...area.questionIndexes],
      topics: finaliseTopicCollection(area.topics),
    }))
    .sort((a, b) => {
      if (b.total !== a.total) return b.total - a.total;
      return a.label.localeCompare(b.label, 'es');
    });
};

/**
 * Computes a rich analytics object for a completed exam.
 *
 * @param {Object} params
 * @param {Array} params.questions - Ordered list of questions shown to the user.
 * @param {Array} [params.userAnswers] - Array of user answers aligned by index (option id/label).
 * @param {Object} [params.markedForReview] - Map of question index -> boolean (flagged during simulacrum).
 * @param {Object} [params.questionNotes] - Map of question index -> { text, confidence } (coaching notes).
 * @param {Array} [params.questionDetails] - Legacy support: array containing per-question detail with userAnswer/status already attached.
 * @returns {Object|null} analytics payload or null when inputs are invalid.
 */
export function computeExamAnalytics({
  questions,
  userAnswers,
  markedForReview,
  questionNotes,
  questionTimes,
  questionDetails,
} = {}) {
  let workingQuestions = questions;
  let workingUserAnswers = userAnswers;
  let reviewMap = markedForReview || {};
  let notesMap = questionNotes || {};
  let timesMap = questionTimes || {};

  if (!Array.isArray(workingQuestions) || workingQuestions.length === 0) {
    if (Array.isArray(questionDetails) && questionDetails.length > 0) {
      workingQuestions = questionDetails.map((detail) => detail.question || detail);
      workingUserAnswers = questionDetails.map((detail) => {
        if (detail.userAnswerId !== undefined) return detail.userAnswerId;
        if (detail.userAnswer !== undefined) return detail.userAnswer;
        return null;
      });
      reviewMap = {};
      questionDetails.forEach((detail, index) => {
        if (detail.flagged) reviewMap[index] = true;
      });
    }
  }

  if (!Array.isArray(workingQuestions) || workingQuestions.length === 0) {
    return null;
  }

  const answers = Array.isArray(workingUserAnswers)
    ? workingUserAnswers
    : Array(workingQuestions.length).fill(null);

  const areaMap = new Map();
  const organMap = new Map();
  const topicMap = new Map();
  const subtopicMap = new Map();
  const detailMap = new Map();

  const questionSummaries = [];
  const statusByIndex = {};
  const flaggedByIndex = {};

  // Pre-computed status/answer data from MySQL-sourced questionDetails
  const precomputed = {};
  if (Array.isArray(questionDetails)) {
    questionDetails.forEach((detail, idx) => {
      if (detail.status) {
        precomputed[idx] = {
          status: detail.status,
          userAnswerLabel: detail.userAnswerLabel || null,
          userAnswerText: detail.userAnswerText || null,
          correctOptionLabel: detail.correctOptionLabel || null,
          correctOptionText: detail.correctOptionText || null,
          confidence: detail.confidence ?? null,
          note: detail.note || null,
        };
      }
    });
  }

  let correctCount = 0;
  let incorrectCount = 0;
  let skippedCount = 0;

  workingQuestions.forEach((rawQuestion, index) => {
    const question = cloneQuestion(rawQuestion || {});
    const options = Array.isArray(question.options) ? question.options : [];
    const answer = answers[index];

    let selectedOption = null;
    if (answer !== undefined && answer !== null) {
      selectedOption =
        options.find((opt) => opt && opt.id === answer) ||
        options.find(
          (opt) =>
            opt &&
            opt.option_label &&
            String(opt.option_label).toLowerCase() === String(answer).toLowerCase()
        );

      if (!selectedOption && typeof answer === 'number' && options[answer]) {
        selectedOption = options[answer];
      }
      if (
        !selectedOption &&
        typeof answer === 'string' &&
        !Number.isNaN(Number.parseInt(answer, 10)) &&
        options[Number.parseInt(answer, 10)]
      ) {
        selectedOption = options[Number.parseInt(answer, 10)];
      }
    }

    const correctOption = options.find((opt) => opt && opt.correct);
    const pre = precomputed[index];
    const status = pre?.status
      ? (pre.status === 'correct' ? STATUS_CORRECT : pre.status === 'incorrect' ? STATUS_INCORRECT : STATUS_SKIPPED)
      : (answer === undefined || answer === null
          ? STATUS_SKIPPED
          : selectedOption && selectedOption.correct
          ? STATUS_CORRECT
          : STATUS_INCORRECT);

    if (status === STATUS_CORRECT) correctCount += 1;
    else if (status === STATUS_INCORRECT) incorrectCount += 1;
    else skippedCount += 1;

    const areaLabel = question.area || DEFAULT_AREA;
    const topicLabel = question.topic || DEFAULT_TOPIC;
    const subtopicLabel = question.subtopic || DEFAULT_SUBTOPIC;
    const detailLabel = question.subtopic_specific || DEFAULT_DETAIL;
    const organLabel = question.organsystem || DEFAULT_ORGAN;
    const flagged = !!reviewMap[index];

    const areaEntry = ensureMapEntry(
      areaMap,
      areaLabel,
      areaLabel,
      (key, label) => ({
        ...createCounterBase(key, label),
        topics: new Map(),
      })
    );
    incrementCounter(areaEntry, status);
    areaEntry.questionIndexes.push(index);

    const topicEntry = ensureMapEntry(
      areaEntry.topics,
      topicLabel,
      topicLabel,
      (key, label) => ({
        ...createCounterBase(key, label),
        subtopics: new Map(),
      })
    );
    incrementCounter(topicEntry, status);
    topicEntry.questionIndexes.push(index);

    const subtopicEntry = ensureMapEntry(
      topicEntry.subtopics,
      subtopicLabel,
      subtopicLabel,
      (key, label) => ({
        ...createCounterBase(key, label),
        details: new Map(),
      })
    );
    incrementCounter(subtopicEntry, status);
    subtopicEntry.questionIndexes.push(index);

    const detailEntry = ensureMapEntry(
      subtopicEntry.details,
      detailLabel,
      detailLabel,
      (key, label) => createCounterBase(key, label)
    );
    incrementCounter(detailEntry, status);
    detailEntry.questionIndexes.push(index);

    const organEntry = ensureMapEntry(
      organMap,
      organLabel,
      organLabel,
      (key, label) => createCounterBase(key, label)
    );
    incrementCounter(organEntry, status);

    const topicGlobalEntry = ensureMapEntry(
      topicMap,
      topicLabel,
      topicLabel,
      (key, label) => createCounterBase(key, label)
    );
    incrementCounter(topicGlobalEntry, status);

    const subtopicGlobalEntry = ensureMapEntry(
      subtopicMap,
      subtopicLabel,
      subtopicLabel,
      (key, label) => createCounterBase(key, label)
    );
    incrementCounter(subtopicGlobalEntry, status);

    const detailGlobalEntry = ensureMapEntry(
      detailMap,
      detailLabel,
      detailLabel,
      (key, label) => createCounterBase(key, label)
    );
    incrementCounter(detailGlobalEntry, status);

    if (flagged) {
      flaggedByIndex[index] = true;
    }
    statusByIndex[index] = status;

    const noteEntry = notesMap[index] || {};
    questionSummaries.push({
      index,
      area: areaLabel,
      topic: topicLabel,
      subtopic: subtopicLabel,
      subtopicSpecific: detailLabel,
      organ: organLabel,
      status,
      flagged,
      note: noteEntry.text || pre?.note || null,
      confidence: noteEntry.confidence ?? pre?.confidence ?? null,
      timeSpentSeconds: timesMap[index] ?? null,
      userAnswerId:
        answer === undefined || answer === null || Number.isNaN(answer) ? null : answer,
      userAnswerLabel: selectedOption ? selectedOption.option_label ?? null : (pre?.userAnswerLabel ?? null),
      userAnswerText: selectedOption ? selectedOption.option_text ?? null : (pre?.userAnswerText ?? null),
      correctOptionLabel: correctOption ? correctOption.option_label ?? null : (pre?.correctOptionLabel ?? null),
      correctOptionText: correctOption ? correctOption.option_text ?? null : (pre?.correctOptionText ?? null),
      question,
    });
  });

  const areas = finaliseAreaCollection(areaMap);
  const areaChartData = areas.map((area) => ({
    area: area.label,
    areaKey: area.key,
    total: area.total,
    correct: area.correct,
    incorrect: area.incorrect,
    skipped: area.skipped,
    accuracy: area.accuracy,
  }));

  const summary = {
    totalQuestions: workingQuestions.length,
    correct: correctCount,
    incorrect: incorrectCount,
    skipped: skippedCount,
    accuracy: roundAccuracy(correctCount, workingQuestions.length),
  };

  return {
    summary,
    areas,
    areaChartData,
    topics: finaliseSimpleCollection(topicMap),
    subtopics: finaliseSimpleCollection(subtopicMap),
    subtopicSpecific: finaliseSimpleCollection(detailMap),
    organSystems: finaliseSimpleCollection(organMap),
    questionSummaries,
    statusByIndex,
    flaggedByIndex,
    statusOrder: STATUS_KEYS,
    generatedAt: Date.now(),
  };
}

/**
 * Convenience helper to obtain analytics from an exam history entry.
 * Ensures backward compatibility with older payloads.
 */
export function ensureAnalyticsFromHistory(exam) {
  if (!exam) return null;
  if (exam.analytics && exam.analytics.summary && exam.analytics.questionSummaries) {
    return exam.analytics;
  }
  const result = computeExamAnalytics({ questionDetails: exam.questionDetails || [] });
  if (result) return result;
  // Fallback: use pre-existing summary from MySQL session data
  if (exam.summary) {
    return {
      summary: exam.summary,
      questionSummaries: [],
      areas: [],
      areaChartData: [],
      topics: [],
      subtopics: [],
      subtopicSpecific: [],
      organSystems: [],
      statusByIndex: {},
      flaggedByIndex: {},
      statusOrder: STATUS_KEYS,
      generatedAt: Date.now(),
    };
  }
  return null;
}

/**
 * Builds a lightweight summary payload combining analytics and metadata.
 */
export function buildExamHistoryPayload({
  examTitle,
  simulacroId = null,
  date,
  time,
  analytics,
  removed = false,
  meta = {},
}) {
  const summary = analytics?.summary || {
    totalQuestions: 0,
    correct: 0,
    incorrect: 0,
    skipped: 0,
    accuracy: 0,
  };

  return {
    id: Date.now(),
    examTitle,
    simulacroId,
    date,
    time,
    removed,
    summary,
    scorePercentage: summary.accuracy,
    analytics,
    questionDetails: analytics?.questionSummaries || [],
    meta,
    createdAt: Date.now(),
  };
}
